// src/components/AsignarRutaModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Chip, Typography, CircularProgress, Alert
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import api from '../api/api';
import PropTypes from 'prop-types'; // Asumiendo que usas PropTypes

// === DECODIFICAR JWT (Función de utilidad, si no está en otro archivo) ===
const decodeToken = (token) => {
    if (!token) return null;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        console.error('Error decodificando token:', e);
        return null;
    }
};

const AsignarRutaModal = ({ open, onClose, qrCode, nombrePaciente }) => {
  const [salas, setSalas] = useState([]);
  const [rutaSeleccionada, setRutaSeleccionada] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ... (handleToggleSala y handleGuardar) ...

  const handleToggleSala = (sala) => {
    setRutaSeleccionada(prev => {
      if (prev.includes(sala)) {
        return prev.filter(s => s !== sala);
      } else {
        return [...prev, sala];
      }
    });
  };
  
  const handleGuardar = async () => {
    setLoading(true);
    setError('');
    // 🚨 AÑADIR TRAZABILIDAD AQUÍ
    const payload = {
        qr_code: qrCode,
        ruta: rutaSeleccionada,
    };

    console.log('--- PAYLOAD ENVIADO A /pacientes/ruta ---');
    console.log('Cuerpo de la Solicitud (payload):', payload);
    console.log('Tipo de Ruta Seleccionada:', Array.isArray(rutaSeleccionada) ? 'Array' : typeof rutaSeleccionada);
    console.log('-------------------------------------------');



    try {
      await api.post('/pacientes/ruta', payload); // Usamos el payload definido
      onClose(true); // Cierra y notifica éxito
    } catch (err) {
      console.error('Error al guardar ruta:', err.response?.data || err.message);
      setError('Error al asignar la ruta. Revisa la consola y el servidor (Error 500).');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (open && qrCode) {
      setRutaSeleccionada([]);
      setError('');
      setLoading(false);
      
      // 1. OBTENER ID DE SUCURSAL DEL USUARIO
      const token = localStorage.getItem('token');
      const decodedToken = decodeToken(token);
      // Asume que el ID de la sucursal del usuario está en el campo 'id_sucursal' del token.
      const idSucursal = decodedToken?.id_sucursal || null; 

      console.log('MODAL ABIERTO - QR:', qrCode, 'Sucursal ID:', idSucursal);

      // 2. CONSTRUIR URL DE CARGA DE SALAS CON FILTRO
      const urlSalas = idSucursal ? `/pacientes/salas?id_sucursal=${idSucursal}` : '/pacientes/salas';
      
      // CARGAR SALAS
      api.get(urlSalas) // 🚨 AHORA UTILIZA EL FILTRO
        .then(res => setSalas(Array.isArray(res.data) ? res.data : []))
        .catch((err) => {
             console.error('Error cargando salas:', err);
             setError('Error cargando salas. Revisa el backend.');
        });

      // CARGAR RUTA ACTIVA (se mantiene igual)
      api.get(`/pacientes/ruta/activa/${qrCode}`)
        .then(res => {
          if (res.data.ruta && res.data.ruta.length > 0) {
            setRutaSeleccionada(res.data.ruta);
          }
        })
        .catch(err => {
          console.warn('No hay ruta activa o error al cargarla:', err);
        });
    }
  }, [open, qrCode]); 

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Asignar Ruta a: {nombrePaciente || qrCode}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="h6" gutterBottom>Salas Disponibles:</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, border: '1px solid #ccc', p: 2, borderRadius: 1 }}>
          {salas.length === 0 && !error ? (
            <CircularProgress size={20} />
          ) : salas.length === 0 ? (
            <Typography color="textSecondary">No se encontraron salas para esta sucursal.</Typography>
          ) : (
            salas.map((sala) => (
              <Chip
                key={sala}
                label={sala}
                onClick={() => handleToggleSala(sala)}
                color={rutaSeleccionada.includes(sala) ? 'secondary' : 'default'}
                variant={rutaSeleccionada.includes(sala) ? 'filled' : 'outlined'}
                size="medium"
                clickable
                icon={rutaSeleccionada.includes(sala) ? <CheckIcon /> : undefined}
              />
            ))
          )}
        </Box>

        {rutaSeleccionada.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2">Orden actual:</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
              {rutaSeleccionada.map((s, i) => (
                <Chip key={i} label={`${i + 1}. ${s}`} size="small" color="primary" />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>

        <Button
          onClick={async () => {
            if (window.confirm('¿Finalizar ruta actual sin completar?')) {
              setLoading(true);
              try {
                await api.post('/pacientes/ruta/finalizar', { qr_code: qrCode });
                onClose(true);
              } catch (err) {
                setError('Error al finalizar ruta');
              } finally {
                setLoading(false);
              }
            }
          }}
          variant="outlined"
          color="warning"
          disabled={loading}
        >
          Finalizar Ruta
        </Button>

        <Button
          onClick={handleGuardar}
          variant="contained"
          color="success"
          disabled={loading || rutaSeleccionada.length === 0}
        >
          {loading ? <CircularProgress size={20} /> : 'Guardar Ruta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AsignarRutaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  qrCode: PropTypes.string.isRequired,
  nombrePaciente: PropTypes.string,
};

export default AsignarRutaModal;